import { useMemo, useState } from 'react';
import ExpandCaret from './ExpandCaret';

// The curated starter list (canonical_classifications WHERE is_preset = 1),
// as a checkbox grid plus a create button.
//
// WHY THIS IS A COMPONENT AND NOT INLINE IN THE FIRST-RUN CARD:
//
// It used to live only there, and that card renders only while a restaurant has
// ZERO classifications (`!hasClassifications`, ItemClassificationPage). So the
// moment a user applied even one suggestion — which creates a classification —
// the card unmounted and the starter list became unreachable for that
// restaurant, permanently. The two doors on that card read as alternatives, but
// they are not: suggestions cover the suppliers Peak recognises, the presets
// give you shelf structure for everything else. Most restaurants want both.
//
// The fallback was worse than "type them yourself". Creating a classification by
// hand runs resolveCanonical() on the name, and a name that does not match an
// alias exactly stores canonical_id = NULL — which, per the comment in
// routes/itemClassifications.js, makes it INVISIBLE to the suggestion engine. So
// rebuilding the list from memory silently degrades future suggestions. That is
// the failure this component exists to prevent.
//
// Same shape as commit 1ce9b52 (ClassificationManager deleted, capability
// unreachable, shipped) — see the header of ClassificationSheet.
export default function PresetClassificationPicker({
  presets = [],
  existing = [],          // the restaurant's current classifications
  onCreate,               // (canonicalIds) => Promise
  busy = false,
  // The first-run card wants this open and loud; the management sheet wants it
  // folded away behind a link, because there it is a rare top-up, not the
  // main event.
  variant = 'inline',     // 'inline' | 'collapsed'
  primary = false,        // render the create button as the primary action
}) {
  const [open, setOpen] = useState(variant === 'inline');

  // Only offer what the restaurant does not already have. Matched on
  // canonical_id, NOT on name: a user who renamed "בשר ועוף" to "בשר" still has
  // that classification, and offering it again would create a duplicate under
  // the canonical name. Falls back to name matching for rows created before
  // canonical_id was returned by the API.
  const missing = useMemo(() => {
    const haveIds = new Set(existing.map(c => c.canonical_id).filter(v => v != null));
    const haveNames = new Set(existing.map(c => (c.name || '').trim().toLowerCase()));
    return presets.filter(p => !haveIds.has(p.canonical_id)
      && !haveNames.has((p.name || '').trim().toLowerCase()));
  }, [presets, existing]);

  // null = untouched, meaning "the RECOMMENDED ones" — not "all of them".
  //
  // The management sheet now passes the whole canonical vocabulary, not just
  // the starters, and it ends in OPEX rows (כוח אדם, שיווק, עמלות ובנקאות…)
  // that are expense categories, not item types — nothing you would ever tag
  // an invoice LINE with. Pre-ticking everything turned the default action
  // into "create 29 classifications", most of them wrong for this screen.
  //
  // So: `is_preset` rows start ticked (the curated starter set — one click
  // still sets up a fresh restaurant), everything else is opt-in. Once a
  // restaurant already has all the starters, nothing is pre-ticked and the
  // list becomes what it should be at that point: a browse-and-pick.
  //
  // Only when the payload carries no is_preset field AT ALL (an older API
  // response, which only ever contained starters) does "all ticked" survive,
  // because that is what that shape used to mean.
  const [picked, setPicked] = useState(null);
  const flagged = missing.some(p => 'is_preset' in p);
  const effective = picked ?? Object.fromEntries(
    missing.map(p => [p.canonical_id, flagged ? Boolean(p.is_preset) : true]),
  );
  const chosen = missing.filter(p => effective[p.canonical_id]);

  // Nothing to add is not an error state, it is the finished state. Say nothing
  // rather than render an empty grid under a heading promising a list.
  if (missing.length === 0) return null;

  const toggle = (id, on) => setPicked({ ...effective, [id]: on });

  if (variant === 'collapsed' && !open) {
    return (
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>
        הוסף מרשימת סיווגים מוכנה ({missing.length})
      </button>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`btn ${primary ? 'btn-primary' : 'btn-secondary'}${variant === 'collapsed' ? ' btn-sm' : ''}`}
          disabled={busy || chosen.length === 0}
          onClick={() => onCreate(chosen.map(p => p.canonical_id))}
        >
          {/* Never "הוסף 0 סיווגים" / "צור 0 סיווגים והתחל" — a disabled
              button whose label counts to zero reads like the screen is
              broken rather than waiting for a choice. With nothing ticked it
              says what to DO instead of what it would do. */}
          {chosen.length === 0
            ? 'בחרו סיווגים להוספה'
            : variant === 'collapsed'
              ? `הוסף ${chosen.length} סיווגים`
              : `צור ${chosen.length} סיווגים והתחל`}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(v => !v)}>
          <ExpandCaret open={open} muted={false} />
          {open ? ' הסתר' : ' בחר אילו סיווגים ליצור'}
        </button>
      </div>

      {open && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {missing.map(p => (
            <label
              key={p.canonical_id}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                borderRadius: 8, cursor: 'pointer', border: '1px solid #dde3ec',
              }}
            >
              <input
                type="checkbox"
                checked={!!effective[p.canonical_id]}
                onChange={e => toggle(p.canonical_id, e.target.checked)}
              />
              {p.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
